import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import GrantContent from '../../../components/GrantContent'
import HtmlEditor from '../../../components/HtmlEditor'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon, PlusIcon } from '../../../template/kartera/icons'
import * as Permisos from '../../../utils/Permisos'
import { EDITOR_EXTENSIONS, useEmailTemplateFormModel } from './email-template-form-model'

const outlineBtnSx = {
    width: 'auto',
    minWidth: 'auto',
    flex: 'none',
    height: 40,
    padding: '0 16px',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    backgroundColor: 'rgb(var(--k-surface))',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line))',
    '& .MuiButton-startIcon': { color: 'inherit' },
    '&:hover': {
        backgroundColor: 'rgb(var(--k-hover))',
        border: '1px solid rgb(var(--k-line))',
        color: 'rgb(var(--k-ink))',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const dangerBtnSx = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-danger))',
    color: '#fff',
    border: 0,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-danger))',
        filter: 'brightness(1.08)',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

const EmailTemplateForm = () => {
    const model = useEmailTemplateFormModel()
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar modelo</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar el modelo de correo? Los envíos automáticos que lo usan dejarán de enviarse.</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelDelete}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.deleteTemplate} disabled={model.deleting}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description="Plantilla del correo que se envía automáticamente cuando ocurre el evento seleccionado."
                    backTo={model.backTo}
                    backLabel="Modelos de correo"
                    actions={model.isEdit ? (
                        <GrantContent permisos={[Permisos.COMUNICADOS_D]}>
                            <Button type="button" variant="contained" startIcon={<DeleteIcon />} sx={dangerBtnSx} onClick={model.askDelete} disabled={model.deleting}>
                                Eliminar
                            </Button>
                        </GrantContent>
                    ) : (
                        <span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>
                    )}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Configuración del modelo</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <TextField className="v2-field" variant="filled" fullWidth required label="Nombre del modelo" value={form.nombre} onChange={(event) => change.nombre(event.target.value)} />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select required
                            label="Evento"
                            helperText="Cuándo se envía el correo."
                            value={form.evento}
                            onChange={(event) => change.evento(event.target.value === '' ? '' : Number(event.target.value))}
                            slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                        >
                            <MenuItem value=""><em>Seleccione</em></MenuItem>
                            {model.eventos.map((evento) => <MenuItem key={evento.id} value={evento.id}>{evento.nombre}</MenuItem>)}
                        </TextField>
                    </div>
                </section>

                {!model.evento && !model.loading && (
                    <section className="k-card">
                        <p className="m-0 text-sm text-k-muted">Selecciona un evento para capturar el asunto, las copias y el cuerpo del correo.</p>
                    </section>
                )}

                {model.evento && (
                    <>
                        <section className="k-card">
                            <h2 className="k-card-title">Envío</h2>
                            <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                                <TextField className="v2-field md:col-span-2" variant="filled" fullWidth required label="Asunto" value={form.asunto} onChange={(event) => change.asunto(event.target.value)} />
                                <TextField className="v2-field" variant="filled" fullWidth type="email" label="Cuenta para copia (CC)" value={form.cuentaCC} onChange={(event) => change.cuentaCC(event.target.value)} />
                                <TextField className="v2-field" variant="filled" fullWidth type="email" label="Cuenta para copia oculta (CCO)" value={form.cuentaCCB} onChange={(event) => change.cuentaCCB(event.target.value)} />
                            </div>
                        </section>

                        <section className="k-card">
                            <h2 className="k-card-title">Cuerpo del correo</h2>
                            {model.campos.length > 0 && (
                                <div className="flex flex-col gap-2">
                                    <span className="text-[13px] text-k-muted">Campos dinámicos: da clic para insertarlos donde está el cursor.</span>
                                    <div className="flex flex-wrap gap-2">
                                        {model.campos.map((campo) => (
                                            <Tooltip key={campo.id} title={campo.descripcion || ''}>
                                                <button
                                                    type="button"
                                                    onClick={() => model.insertField(campo.nombre)}
                                                    className="inline-flex min-h-[34px] items-center gap-1.5 rounded-full border border-k-line-strong bg-k-surface px-3 text-[13px] font-semibold text-k-ink-2 hover:border-k-primary hover:bg-k-accent-soft hover:text-k-accent-ink"
                                                >
                                                    <PlusIcon size={12} />
                                                    @{campo.nombre}
                                                </button>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <HtmlEditor
                                label="Mensaje"
                                required
                                value={form.cuerpo}
                                onChange={change.cuerpo}
                                extensions={EDITOR_EXTENSIONS}
                                onReady={model.setEditor}
                                helperText="Los campos dinámicos se reemplazan con los datos de la póliza o el recibo al enviar."
                            />
                        </section>
                    </>
                )}

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                        <button
                            type="button"
                            className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                            onClick={model.cancel}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={model.busy || !model.evento}
                            className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <CheckIcon />
                            Guardar modelo
                        </button>
                    </div>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default EmailTemplateForm
